import { useSelector, useDispatch } from 'react-redux';
import { Card, Modal, Progress } from 'antd';
import { updateCurrentPractitionerSettings } from 'src/store/actions/practitioner.action';
import { currentPractitionerEntitlements as setCurrentPractitionerEntitlements } from 'src/store/slices/practitioner.slice';
import { useState, useRef, useEffect } from 'react';
import {
  CheckCircleOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  UnorderedListOutlined,
  AlignLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { get, set } from 'lodash';
import { formatOptions } from '../../utils/encounterConstant';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

const icons = {
  prose: <AlignLeftOutlined />,
  bulleted: <UnorderedListOutlined />,
  generic: <AlignLeftOutlined />,
  problem_focused: <UnorderedListOutlined />,
};

const sectionFormatQues = [
  {
    title: 'HPI',
    type: 'hpi',
    options: formatOptions.map((option) => ({
      ...option,
      icon: icons[option.value],
      description: (
        <div className="note-example">
          {option.value === 'prose' ? (
            <p>
              The patient presents for a routine follow-up visit. The patient's
              chronic conditions include hypertension, hyperlipidemia, obesity,
              and an anxiety disorder. These conditions have been previously
              diagnosed and are being managed with ongoing treatment plans. The
              patient's medical history also includes a vitamin D deficiency.
              Regular monitoring and adjustments to the treatment plans are made
              as necessary to ensure optimal management of these conditions. The
              patient's overall health status and any new concerns will be
              assessed during this visit to provide comprehensive and
              personalized care.
            </p>
          ) : (
            <div>
              <ul>
                <li>- Routine follow-up visit</li>
                <li>
                  - Chronic conditions: hypertension, hyperlipidemia, obesity,
                  anxiety disorder
                </li>
                <li>
                  - Ongoing management with treatment plans for chronic
                  conditions
                </li>
                <li>- Medical history includes vitamin D deficiency</li>
                <li>
                  - Regular monitoring and adjustments to treatment plans as
                  needed
                </li>
                <li>
                  - Assessment of overall health status and new concerns during
                  this visit
                </li>
              </ul>
            </div>
          )}
        </div>
      ),
    })),
  },
  {
    title: 'Subjective',
    type: 'subjective',
    options: formatOptions.map((option) => ({
      ...option,
      icon: icons[option.value],
      description: (
        <div className="note-example">
          {option.value === 'prose' ? (
            <div>
              <p>
                The patient presents with a chief complaint of persistent cough
                and fatigue, ongoing for the past 5 days. The cough is described
                as dry and non-productive, worsening at night. Fatigue is
                reported as moderate and interfering with daily activities. The
                patient denies fever, chills, or shortness of breath. Sleep has
                been disrupted due to coughing fits. The patient notes increased
                stress levels due to an upcoming work deadline. There are no
                reported changes in chronic conditions or medications. Overall,
                the patient appears fatigued but is able to provide a clear
                history of the present illness.
              </p>
            </div>
          ) : (
            <div>
              <ul>
                <li>
                  - Chief complaint: Persistent cough and fatigue for 5 days
                </li>
                <li>- Cough: Dry, non-productive; worse at night</li>
                <li>- Fatigue: Moderate, interfering with daily activities</li>
                <li>- No fever, chills, or shortness of breath</li>
                <li>- Sleep: Disrupted due to coughing fits</li>
                <li>- Stress levels increased due to upcoming work deadline</li>
                <li>- No changes in chronic conditions or medications</li>
              </ul>
            </div>
          )}
        </div>
      ),
    })),
  },
];

const assessmentFormatOptions = [
  {
    label: 'Problem-Focused',
    value: 'problem_focused',
    icon: icons['problem_focused'],
    description: (
      <div className="note-example">
        <ul>
          <li>
            - Acute Bronchitis: The patient's symptoms and their duration
            suggest a viral origin. There are no current signs of pneumonia or
            other complications, but close monitoring is advised.
          </li>
          <li>
            - Anxiety Disorder: A mild exacerbation is noted, likely due to
            increased work stress. This may be contributing to the reported
            sleep disturbances and exacerbating fatigue symptoms.
          </li>
          <li>
            - Obesity (BMI 32.5): While not presenting any acute issues, the
            patient's obesity may be a contributing factor to their general
            fatigue. Ongoing management and lifestyle modifications are
            necessary to address this chronic health concern.
          </li>
        </ul>
      </div>
    ),
  },
  {
    label: 'Narrative',
    value: 'generic',
    icon: icons['generic'],
    description: (
      <div className="note-example">
        <p>
          The patient presents with acute bronchitis, likely of viral origin, as
          the primary acute concern. Hypertension is noted to be well-controlled
          on the current medication regimen. Obesity is present with a BMI of
          32.5, which may be contributing to overall health issues.
          Hyperlipidemia remains stable on current statin therapy. The patient's
          anxiety disorder shows a mild exacerbation. Vitamin D deficiency has
          been identified, and supplementation will be initiated. Sleep
          disturbance is noted, likely due to the combination of cough from
          bronchitis and increased anxiety symptoms.
        </p>
      </div>
    ),
  },
];

const questions = [
  ...sectionFormatQues.map((section) => ({
    name: section.title,
    title: (
      <>
        How would you like the{' '}
        <span className="font-semibold text-[#00d090]">{section.title}</span>{' '}
        section to be formatted?
      </>
    ),
    key: `section_format.${section.type}`,
    type: 'radio',
    options: section.options,
  })),
  {
    name: 'Assessment',
    title: (
      <>
        How would you like the{' '}
        <span className="font-semibold text-[#00d090]">Assessment</span> section
        to be?
      </>
    ),
    key: 'assessment_type',
    type: 'radio',
    options: assessmentFormatOptions,
  },
];

function NotePreferencesModal({ open }) {
  const dispatch = useDispatch();
  const {
    currentPractitionerSettings,
    currentPractitionerSettingsLoading,
    currentPractitionerEntitlements,
  } = useSelector((state) => state?.practitionerState);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [values, setValues] = useState({});
  const titleRef = useRef(null);
  const submitButtonRef = useRef(null);
  const { user } = useAuth();

  const updateNotePreferences = async () => {
    analytics.track(
      `Clicked Save And Try A Demo Button In Note Preferences Modal`,
      {
        note_preferences: values,
        email: user?.email,
      }
    );
    dispatch(
      updateCurrentPractitionerSettings({
        note_preferences: {
          ...currentPractitionerSettings.note_preferences,
          ...values,
        },
      })
    ).then(() => {
      dispatch(
        setCurrentPractitionerEntitlements({
          ...currentPractitionerEntitlements,
          request_note_preferences: false,
        })
      );
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Lead to the submit button when the last question is answered
      submitButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const previousQuestion = () => {
    analytics.track(`Clicked Back Button In Note Preferences Modal`, {
      question: questions[currentQuestionIndex].name,
      email: user?.email,
    });
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  useEffect(() => {
    if (open) {
      // scroll to top of the modal when new question is presented
      titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentQuestionIndex]);

  const renderQuestion = (
    { options, title, type, name },
    { value, onChange }
  ) => {
    if (type === 'radio') {
      return (
        <div className="flex flex-col gap-3">
          <div className="text-sm md:text-lg text-center">{title}</div>
          <div className="flex gap-3 flex-wrap md:flex-nowrap">
            {options.map((option) => {
              const isSelected = value === option.value;

              const borderColor = isSelected
                ? 'border-[#00d090] hover:border-[#00d090]'
                : 'border-[#eeeeee] hover:border-[#eeeeee]';

              const bgColor = isSelected
                ? '[&_.ant-card-body]:bg-[#00d09008]'
                : '';

              return (
                <Card
                  key={option.value}
                  styles={{ body: { padding: 16 } }}
                  hoverable
                  bordered
                  className={`w-full max-w-[100%] border-2 ${bgColor} ${borderColor}`}
                  onClick={() => {
                    analytics.track(
                      `Selected ${option.label} For ${name} In Note Preferences Modal`,
                      {
                        option: option.value,
                        email: user?.email,
                      }
                    );
                    onChange(option.value);
                  }}
                >
                  <div className="w-full">
                    <div
                      className={`flex items-center justify-between gap-2 font-semibold ${
                        isSelected ? 'text-[#00d090]' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex text-lg items-center p-2 justify-center w-8 h-8 rounded-lg ${
                            isSelected
                              ? 'bg-[#00d09025] text-[#00d090]'
                              : 'bg-gray-100'
                          }`}
                        >
                          {option.icon}
                        </div>
                        <div className="text-lg">{option.label}</div>
                      </div>
                      {isSelected ? (
                        <CheckCircleFilled className="text-2xl" />
                      ) : null}
                    </div>
                    <div className="pt-3">
                      <div className="text-lg font-medium text-[#00d090]">
                        {name}
                      </div>
                      <div>{option.description}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      );
    }
  };

  const onChange = (value, key) => {
    setValues((values) => set({ ...values }, key, value));
    setTimeout(() => {
      nextQuestion();
    }, 300);
  };

  const currentQuestionValue = get(values, questions[currentQuestionIndex].key);

  return (
    <Modal
      centered
      classNames={{ content: 'p-[0] !important' }}
      closable={false}
      width={900}
      open={open}
      footer={
        <div className="flex justify-between empty:hidden">
          {currentQuestionIndex > 0 && (
            <button
              className="ih-button-ghost rounded-full flex items-center gap-2"
              onClick={previousQuestion}
            >
              <ArrowLeftOutlined />
              Back
            </button>
          )}
          {currentQuestionValue &&
            currentQuestionIndex < questions.length - 1 && (
              <button
                disabled={currentPractitionerSettingsLoading}
                className="ih-button-ghost rounded-full right-0 ml-auto flex items-center gap-2"
                onClick={nextQuestion}
              >
                Next
                <ArrowRightOutlined />
              </button>
            )}
          {currentQuestionValue &&
            currentQuestionIndex === questions.length - 1 && (
              <button
                disabled={currentPractitionerSettingsLoading}
                ref={submitButtonRef}
                className="ih-button-primary rounded-full flex items-center gap-2"
                onClick={updateNotePreferences}
              >
                Save and try a demo
                <CheckCircleOutlined />
              </button>
            )}
        </div>
      }
    >
      <div className="flex flex-col justify-center gap-3">
        <Progress
          showInfo={false}
          strokeWidth={20}
          strokeColor={'#00d090'}
          percent={Math.round((currentQuestionIndex * 100) / questions.length)}
        />
        <div className="flex flex-col gap-3 w-full">
          <div
            ref={titleRef}
            className="font-semibold text-sm text-center md:text-xl"
          >
            Setup your note preferences
          </div>
          <div className="flex flex-col">
            {renderQuestion(questions[currentQuestionIndex], {
              value: currentQuestionValue,
              onChange: (value) =>
                onChange(value, questions[currentQuestionIndex].key),
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { NotePreferencesModal };
